import { enableProdMode } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import {
  utmCampaignFromUrl,
  utmMediumFromUrl,
  utmSourceFromUrl,
} from '@common';
import {
  DOMAIN_CONFIG,
  DomainConfig,
  DomainId,
  domainIdFromDomain,
  RUNTIME_CONFIG,
  RuntimeConfig,
} from '@config';
import { AppComponent } from './app/components/app/app.component';
import { provideApp } from './app/providers/app.provider';
import { environment } from './environments/environment';

const ERROR_ROUTE_PATH = 'error';
const LOCALHOST_HOSTNAMES = ['localhost', '127.0.0.1'];

export async function bootstrap() {
  if (environment.production) {
    enableProdMode();
  }

  try {
    const domainId = resolveDomainId();

    if (!domainId) {
      const errorPath = getErrorPath();

      if (window.location.pathname !== errorPath) {
        window.location.replace(errorPath);
        return;
      }

      const runtimeConfig = await loadRuntimeConfig();
      startApp({}, runtimeConfig);
      return;
    }

    const [domainConfig, runtimeConfig] = await Promise.all([
      loadDomainConfig(domainId),
      loadRuntimeConfig(),
    ]);

    startApp(domainConfig, runtimeConfig);
  } catch (error) {
    console.error(error);
  }
}

function startApp(
  domainConfig: DomainConfig | Record<string, never>,
  runtimeConfig: RuntimeConfig,
): void {
  bootstrapApplication(AppComponent, {
    providers: [
      {
        provide: DOMAIN_CONFIG,
        useValue: domainConfig,
      },
      {
        provide: RUNTIME_CONFIG,
        useValue: runtimeConfig,
      },
      provideApp(),
    ],
  }).catch((error) => console.error(error));
}

function getErrorPath(): string {
  return new URL(ERROR_ROUTE_PATH, document.baseURI).pathname;
}

function resolveDomainId(): DomainId | undefined {
  const mappedDomainId = domainIdFromDomain(environment.domainIdMapping);

  if (mappedDomainId) {
    return mappedDomainId;
  }

  const isLocalhost = LOCALHOST_HOSTNAMES.includes(window.location.hostname);

  if (!isLocalhost || environment.production) {
    return undefined;
  }

  return environment.defaultDomainId;
}

async function loadDomainConfig(domainId: DomainId): Promise<DomainConfig> {
  const domainConfigPath = `configs/${environment.appVariantConfigPath}/domains/${domainId}.json`;
  const utmCampaign = utmCampaignFromUrl();
  const utmMedium = utmMediumFromUrl();
  const utmSource = utmSourceFromUrl();

  if (!utmCampaign || !utmMedium || !utmSource) {
    const response = await fetch(domainConfigPath);
    return response.json();
  }

  let subDomainConfigPath = '';

  subDomainConfigPath =
    domainId !== utmSource.toLowerCase() && utmMedium === 'app'
      ? `configs/${environment.appVariantConfigPath}/domains/${domainId}-${utmSource.toLowerCase()}.json`
      : domainConfigPath;

  try {
    const response = await fetch(subDomainConfigPath);
    if (!response.ok) {
      throw new Error('Subdomain config not found');
    }
    return response.json();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    const response = await fetch(domainConfigPath);
    return response.json();
  }
}

async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  const response = await fetch(
    `configs/${environment.appVariantConfigPath}/deployments/config.${environment.name}.json`,
  );
  return response.json();
}
